import React from "react";

const PLAYER_STATS_GRID_ZOOM_LEVELS = [0.45, 0.6, 0.75, 1, 1.15];
export const PLAYER_STATS_GRID_DEFAULT_ZOOM_INDEX = 3;

type PlayerStatsGridLayoutOptions = {
  mobileMode: boolean;
  compactTableMode: boolean;
  gridZoomIndex: number;
};

export function getPlayerStatsGridLayout({
  mobileMode,
  compactTableMode,
  gridZoomIndex,
}: PlayerStatsGridLayoutOptions) {
  const gridZoomScale = PLAYER_STATS_GRID_ZOOM_LEVELS[gridZoomIndex] ?? 1;
  const scalePx = (value: number) => Math.max(1, Math.round(value * gridZoomScale));
  const cellFontSize = scalePx(compactTableMode ? 10 : 12);

  return {
    gridZoomScale,
    scalePx,
    firstColumnWidth: scalePx(compactTableMode ? 36 : mobileMode ? 48 : 56),
    secondColumnWidth: scalePx(compactTableMode ? 50 : mobileMode ? 48 : 74),
    statColumnWidth: scalePx(compactTableMode ? 40 : 56),
    headerFontSize: scalePx(compactTableMode ? 8 : 10),
    cellFontSize,
    cellPadding: `${scalePx(10)}px ${scalePx(8)}px`,
    statPillPadding: `${scalePx(6)}px ${compactTableMode ? scalePx(2) : scalePx(6)}px`,
    statPillRadius: scalePx(7),
  };
}

type PlayerStatsGridZoomControlProps = {
  gridZoomIndex: number;
  setGridZoomIndex: React.Dispatch<React.SetStateAction<number>>;
  label: string;
  mobileMode: boolean;
  compactMode?: boolean;
};

export function PlayerStatsGridZoomControl({
  gridZoomIndex,
  setGridZoomIndex,
  label,
  mobileMode,
  compactMode = false,
}: PlayerStatsGridZoomControlProps) {
  const buttonPadding = compactMode ? "3px 5px" : mobileMode ? "5px 6px" : "6px 9px";
  const buttonFontSize = compactMode ? 12 : mobileMode ? 14 : 16;
  const iconBoxWidth = compactMode ? 16 : mobileMode ? 18 : 24;
  const iconBoxHeight = compactMode ? 20 : mobileMode ? 24 : 26;
  const iconSize = compactMode ? 10 : mobileMode ? 12 : 14;

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        padding: compactMode ? 1 : mobileMode ? 1 : 2,
        borderRadius: 999,
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.08)",
        flex: mobileMode ? "0 0 auto" : undefined,
      }}
    >
      <button
        type="button"
        aria-label={`Zoom out ${label} grid`}
        onClick={() => setGridZoomIndex((current) => Math.max(0, current - 1))}
        disabled={gridZoomIndex === 0}
        style={{
          appearance: "none",
          border: "none",
          cursor: gridZoomIndex > 0 ? "pointer" : "default",
          borderRadius: 999,
          padding: buttonPadding,
          background: "transparent",
          color: gridZoomIndex > 0 ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.28)",
          fontWeight: 900,
          fontSize: buttonFontSize,
          lineHeight: 1,
        }}
      >
        -
      </button>
      <div
        aria-hidden
        style={{
          width: iconBoxWidth,
          height: iconBoxHeight,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255,255,255,0.92)",
        }}
      >
        <svg viewBox="0 0 24 24" width={iconSize} height={iconSize}>
          <circle cx="10.5" cy="10.5" r="5.5" fill="none" stroke="currentColor" strokeWidth="2" />
          <path
            d="M15 15l4.5 4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <button
        type="button"
        aria-label={`Zoom in ${label} grid`}
        onClick={() =>
          setGridZoomIndex((current) => Math.min(PLAYER_STATS_GRID_ZOOM_LEVELS.length - 1, current + 1))
        }
        disabled={gridZoomIndex === PLAYER_STATS_GRID_ZOOM_LEVELS.length - 1}
        style={{
          appearance: "none",
          border: "none",
          cursor: gridZoomIndex < PLAYER_STATS_GRID_ZOOM_LEVELS.length - 1 ? "pointer" : "default",
          borderRadius: 999,
          padding: buttonPadding,
          background: "transparent",
          color:
            gridZoomIndex < PLAYER_STATS_GRID_ZOOM_LEVELS.length - 1
              ? "rgba(255,255,255,0.82)"
              : "rgba(255,255,255,0.28)",
          fontWeight: 900,
          fontSize: buttonFontSize,
          lineHeight: 1,
        }}
      >
        +
      </button>
    </div>
  );
}
